import { useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import type {
  WorkerOrder,
  WorkerOrdersResponse,
  WorkerTerminalError,
  SortOption,
  ItemCountFilter,
} from '../types/worker';

interface UseWorkerOrdersConfig {
  adminId: string | null;
  pageSize?: number;
  sortBy?: SortOption;
  itemCountFilter?: ItemCountFilter;
}

interface UseWorkerOrdersReturn {
  orders: WorkerOrder[];
  loading: boolean;
  error: WorkerTerminalError | null;
  totalCount: number;
  currentPage: number;
  hasMore: boolean;
  loadOrders: (page?: number) => Promise<void>;
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;
  clearError: () => void;
}

function parseError(err: unknown): WorkerTerminalError {
  if (err instanceof Error) {
    const message = err.message.toLowerCase();
    if (message.includes('network') || message.includes('fetch') || message.includes('connection')) {
      return {
        type: 'connection',
        message: 'Unable to connect. Please check your internet connection.',
        retryable: true,
      };
    }
    if (message.includes('auth') || message.includes('jwt') || message.includes('token')) {
      return {
        type: 'auth',
        message: 'Your session has expired. Please log in again.',
        retryable: false,
      };
    }
  }
  return {
    type: 'data',
    message: 'Unable to load orders. Please try again.',
    retryable: true,
  };
}

export function useWorkerOrders({
  adminId,
  pageSize = 25,
  sortBy = 'oldest',
  itemCountFilter = 'all',
}: UseWorkerOrdersConfig): UseWorkerOrdersReturn {
  const [orders, setOrders] = useState<WorkerOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<WorkerTerminalError | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);

  const loadOrders = useCallback(
    async (page: number = 0) => {
      if (!adminId) return;

      setLoading(true);
      setError(null);

      try {
        const offset = page * pageSize;

        const { data, error: rpcError } = await supabase.rpc('get_worker_orders_to_pick_v2', {
          p_admin_id: adminId,
          p_limit: pageSize,
          p_offset: offset,
          p_sort_by: sortBy,
          p_item_count_filter: itemCountFilter,
        });

        if (rpcError) {
          throw rpcError;
        }

        if (data && data.length > 0) {
          const mappedOrders: WorkerOrder[] = data.map((row: any) => ({
            id: row.id,
            shopify_order_id: row.shopify_order_id,
            order_number: row.order_number,
            customer_name: row.customer_name,
            customer_first_name: row.customer_first_name,
            customer_last_name: row.customer_last_name,
            customer_email: row.customer_email,
            order_date: row.order_date,
            total_price: row.total_price,
            currency: row.currency,
            financial_status: row.financial_status,
            fulfillment_status: row.fulfillment_status,
            line_items: row.line_items || [],
            shipping_address: row.shipping_address,
          }));

          if (page === 0) {
            setOrders(mappedOrders);
          } else {
            setOrders((prev) => [...prev, ...mappedOrders]);
          }

          setTotalCount(data[0].total_count || 0);
          setCurrentPage(page);
        } else {
          if (page === 0) {
            setOrders([]);
            setTotalCount(0);
          }
        }
      } catch (err) {
        console.error('Failed to load orders:', err);
        setError(parseError(err));
      } finally {
        setLoading(false);
      }
    },
    [adminId, pageSize, sortBy, itemCountFilter]
  );

  const loadMore = useCallback(async () => {
    const nextPage = currentPage + 1;
    const loadedCount = (currentPage + 1) * pageSize;
    if (loadedCount >= totalCount) return;
    await loadOrders(nextPage);
  }, [currentPage, pageSize, totalCount, loadOrders]);

  const refresh = useCallback(async () => {
    setOrders([]);
    setCurrentPage(0);
    await loadOrders(0);
  }, [loadOrders]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const hasMore = orders.length < totalCount;

  return {
    orders,
    loading,
    error,
    totalCount,
    currentPage,
    hasMore,
    loadOrders,
    loadMore,
    refresh,
    clearError,
  };
}
